import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Nav, Navbar, Table, NavDropdown } from 'react-bootstrap';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { adminAPI, groupsAPI } from '../services/api';
import axios from 'axios';

// Import components
import GroupForm from '../components/GroupForm';
import CampaignForm from '../components/CampaignForm';
import CoordinatorForm from '../components/CoordinatorForm';
import ChangePassword from '../components/ChangePassword';
import ConfirmationModal from '../components/ConfirmationModal';
import GroupDetail from './GroupDetail';
import OrdersList from './OrdersList';
import OrderDetail from './OrderDetail';

// Dashboard Components
const DashboardHome = () => {
  const [stats, setStats] = useState({
    groupCount: 0,
    orderCount: 0,
    campaignCount: 0,
    coordinatorCount: 0,
    totalSales: 0,
    activeCampaigns: 0,
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminAPI.getStats();
        setStats(response.data.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading statistics...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <h2 className="mb-4">Dashboard Overview</h2>
      
      <Row>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">{stats.groupCount}</div>
            <div className="stats-label">Total Groups</div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">${stats.totalSales.toFixed(2)}</div>
            <div className="stats-label">Total Sales</div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">{stats.openOrdersCount}</div>
            <div className="stats-label">Open Orders</div>
          </Card>
        </Col>
      </Row>
      
      <Row>
        <Col md={12}>
          <Card className="mb-4">
            <Card.Header>Recent Orders</Card.Header>
            <Card.Body>
              {stats.recentOrders.length > 0 ? (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Group Code</th>
                      <th>Seller Name</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentOrders.map((order) => (
                      <tr key={order._id}>
                        <td>{order.shopifyOrderNumber || order.shopifyOrderId}</td>
                        <td>{order.groupCode}</td>
                        <td>{order.studentName}</td>
                        <td>${order.totalAmount.toFixed(2)}</td>
                        <td>{order.status}</td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Link to={`/admin/orders/${order._id}`}>
                            <Button variant="outline-primary" size="sm">View</Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-center">No recent orders</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Groups Management
const GroupsList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await groupsAPI.getAllGroups();
        setGroups(response.data.data);
      } catch (err) {
        console.error('Error fetching groups:', err);
        setError('Failed to load groups');
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading groups...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Groups</h2>
        <Link to="/admin/groups/new">
          <Button variant="primary">Add New Group</Button>
        </Link>
      </div>
      
      {groups.length > 0 ? (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Organization</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group._id}>
                <td>{group.name}</td>
                <td>{group.code}</td>
                <td>{group.organizationName}</td>
                <td>{group.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <Link to={`/admin/groups/${group._id}`}>
                    <Button variant="outline-primary" size="sm">View</Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-center">No groups found. Create your first group!</p>
      )}
    </div>
  );
};

// Coordinators Management
const CoordinatorsList = () => {
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [coordinatorToDelete, setCoordinatorToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');

  useEffect(() => {
    fetchCoordinators();
  }, []);

  const fetchCoordinators = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllCoordinators();
      setCoordinators(response.data.data);
    } catch (err) {
      console.error('Error fetching coordinators:', err);
      setError('Failed to load coordinators');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (coordinator) => {
    setCoordinatorToDelete(coordinator);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      console.log('Deleting coordinator with ID:', coordinatorToDelete.id);
      
      // Close the modal first to provide better UX
      setShowDeleteModal(false);
      
      // Make the API call with error handling
      try {
        await adminAPI.deleteCoordinator(coordinatorToDelete.id);
        
        // Update the UI to remove the deleted coordinator
        setCoordinators(coordinators.filter(c => c.id !== coordinatorToDelete.id));
        setDeleteSuccess(`Coordinator ${coordinatorToDelete.name} has been deleted successfully.`);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setDeleteSuccess('');
        }, 3000);
      } catch (apiError) {
        console.error('API error:', apiError);
        setDeleteError(`Failed to delete coordinator: ${apiError.response?.data?.message || apiError.message}`);
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setDeleteError('');
        }, 5000);
      }
    } catch (err) {
      console.error('Error in delete process:', err);
      setDeleteError(`An unexpected error occurred. Please try again.`);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setDeleteError('');
      }, 5000);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading coordinators...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Coordinators</h2>
        <Link to="/admin/coordinators/new">
          <Button variant="primary">Add New Coordinator</Button>
        </Link>
      </div>
      
      {deleteSuccess && (
        <div className="alert alert-success">{deleteSuccess}</div>
      )}
      
      {deleteError && (
        <div className="alert alert-danger">{deleteError}</div>
      )}
      
      {coordinators.length > 0 ? (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Group</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coordinators.map((coordinator) => (
              <tr key={coordinator.id}>
                <td>{coordinator.name}</td>
                <td>{coordinator.email}</td>
                <td>{coordinator.group?.name || 'N/A'}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Link to={`/admin/coordinators/${coordinator.id}`}>
                      <Button variant="outline-primary" size="sm">View</Button>
                    </Link>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => handleDeleteClick(coordinator)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-center">No coordinator found. Create your first coordinator!</p>
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Coordinator"
        message={`Are you sure you want to delete coordinator ${coordinatorToDelete?.name}? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

// Main Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('user');
    
    // Redirect to login
    navigate('/admin/login');
  };

  const handleChangePasswordSuccess = () => {
    // Optional: Show a success message or perform any additional actions
    console.log('Password changed successfully');
  };

  return (
    <div>
      <Navbar bg="dark" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand as={Link} to="/admin">Little Lambs Admin Portal</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link as={Link} to="/admin" active={location.pathname === '/admin'}>
                Dashboard
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/groups" active={location.pathname.startsWith('/admin/groups')}>
                Groups
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/coordinators" active={location.pathname.startsWith('/admin/coordinators')}>
                Coordinators
              </Nav.Link>
              <Nav.Link as={Link} to="/admin/orders" active={location.pathname.startsWith('/admin/orders')}>
                Orders
              </Nav.Link>
            </Nav>
            <Nav>
              {user && (
                <NavDropdown title={`${user.name}`} id="user-nav-dropdown" align="end">
                  <NavDropdown.Item onClick={() => setShowChangePassword(true)}>
                    Change Password
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="dashboard-container">
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/groups" element={<GroupsList />} />
          <Route path="/groups/new" element={<GroupForm />} />
          <Route path="/groups/:id" element={<GroupDetail />} />
          <Route path="/groups/:id/edit" element={<GroupForm isEdit={true} />} />
          <Route path="/groups/:groupId/campaigns/new" element={<CampaignForm />} />
          <Route path="/groups/:groupId/campaigns/:campaignId/edit" element={<CampaignForm isEdit={true} />} />
          <Route path="/coordinators" element={<CoordinatorsList />} />
          <Route path="/coordinators/new" element={<CoordinatorForm />} />
          <Route path="/coordinators/:id" element={<CoordinatorForm isEdit={true} />} />
          <Route path="/orders" element={<OrdersList />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
        </Routes>
      </Container>

      {/* Change Password Modal */}
      <ChangePassword
        show={showChangePassword}
        onHide={() => setShowChangePassword(false)}
        onSuccess={handleChangePasswordSuccess}
      />
    </div>
  );
};

export default AdminDashboard;
